export type FoodMakerApiRecord = Record<string, unknown>;
export type FoodMakersStatusLoadStatus = 'idle' | 'loading' | 'success' | 'error';

export interface FoodMakersStatusDataState {
  records: FoodMakerApiRecord[];
  status: FoodMakersStatusLoadStatus;
  errorMessage: string | null;
  language: string | null;
}

export type FoodMakersSortDirection = 'asc' | 'desc';
export interface FoodMakersSortConfig { key: string; direction: FoodMakersSortDirection; }
export interface FoodMakerLocationGroup { location: string; records: FoodMakerApiRecord[]; }
export interface FoodMakersTableLayout {
  columnWidths: Record<string, number>;
  rowHeights: Record<string, number>;
  columnOrder: string[];
  mobileFieldOrder: string[];
  rowOrderByLocation: Record<string, string[]>;
}

export const FOOD_MAKERS_STATUS_LAYOUT_STORAGE_KEY = 'ifood.foodMakersStatus.tableLayout.v2';
export const LEGACY_FOOD_MAKERS_STATUS_LAYOUT_STORAGE_KEY = 'ifood.foodMakersStatus.tableLayout.v1';
export const DEFAULT_FOOD_MAKER_COLUMN_WIDTH = 220;
export const DEFAULT_FOOD_MAKER_NAME_COLUMN_WIDTH = 260;
export const DEFAULT_FOOD_MAKER_ROW_HEIGHT = 88;
export const MINIMUM_FOOD_MAKER_COLUMN_WIDTH = 100;
export const MAXIMUM_FOOD_MAKER_COLUMN_WIDTH = 1200;
export const MINIMUM_FOOD_MAKER_ROW_HEIGHT = 48;
export const MAXIMUM_FOOD_MAKER_ROW_HEIGHT = 800;
export const UNSPECIFIED_FOOD_MAKER_LOCATION = 'Unspecified location';
export const FOOD_MAKER_STOPPED_FIELD = 'is_stop_working_temporary';
export const LEGACY_FOOD_MAKER_STOPPED_FIELD = 'is_stopped_working_temporary';
export const FOOD_MAKER_SUPPLIER_TYPE_FIELD = 'supplier_type';

const EMPTY_TABLE_LAYOUT: FoodMakersTableLayout = {
  columnWidths: {}, rowHeights: {}, columnOrder: [], mobileFieldOrder: [], rowOrderByLocation: {},
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Normalizes supported response envelopes without discarding backend fields. */
export function normalizeFoodMakersPayload(payload: unknown): FoodMakerApiRecord[] {
  const candidateRecords = Array.isArray(payload)
    ? payload
    : isRecord(payload) && Array.isArray(payload.food_makers) ? payload.food_makers : [];
  return candidateRecords.filter(isRecord);
}

/** Discovers all top-level backend fields, retaining the name key first. */
export function discoverFoodMakerColumns(records: FoodMakerApiRecord[]): string[] {
  const keys = new Set<string>();
  records.forEach((record) => Object.keys(record).forEach((key) => keys.add(key)));
  keys.delete('name_code');
  keys.delete(FOOD_MAKER_STOPPED_FIELD);
  keys.delete(LEGACY_FOOD_MAKER_STOPPED_FIELD);
  return [
    'name_code',
    ...(records.some((record) => Object.prototype.hasOwnProperty.call(record, FOOD_MAKER_STOPPED_FIELD))
      ? [FOOD_MAKER_STOPPED_FIELD]
      : records.some((record) => Object.prototype.hasOwnProperty.call(record, LEGACY_FOOD_MAKER_STOPPED_FIELD))
        ? [LEGACY_FOOD_MAKER_STOPPED_FIELD]
        : []),
    ...Array.from(keys).sort((left, right) => left.localeCompare(right)),
  ];
}

/**
 * Resolves the operational state used for visual status emphasis. Stopped
 * partners take precedence because their availability needs immediate notice.
 */
export function getFoodMakerStatusStyle(record: FoodMakerApiRecord): 'stopped' | 'delivery' | null {
  if (record[FOOD_MAKER_STOPPED_FIELD] === true || record[LEGACY_FOOD_MAKER_STOPPED_FIELD] === true) return 'stopped';
  return typeof record[FOOD_MAKER_SUPPLIER_TYPE_FIELD] === 'string'
    && record[FOOD_MAKER_SUPPLIER_TYPE_FIELD].trim().toLocaleLowerCase() === 'delivery'
    ? 'delivery'
    : null;
}

/** Reconciles persisted orders with dynamic backend fields. */
export function reconcileFoodMakerOrder(discoveredKeys: string[], savedOrder: string[], fixedFirstKey?: string): string[] {
  const available = new Set(discoveredKeys);
  const ordered = savedOrder.filter((key, index) => available.has(key) && savedOrder.indexOf(key) === index);
  const combined = [...ordered, ...discoveredKeys.filter((key) => !ordered.includes(key))];
  return fixedFirstKey && available.has(fixedFirstKey)
    ? [fixedFirstKey, ...combined.filter((key) => key !== fixedFirstKey)]
    : combined;
}

function normalizeForStableJson(value: unknown, seen: WeakSet<object>): unknown {
  if (Array.isArray(value)) {
    if (seen.has(value)) return '[Circular]';
    seen.add(value);
    const normalized = value.map((item) => normalizeForStableJson(item, seen));
    seen.delete(value);
    return normalized;
  }
  if (isRecord(value)) {
    if (seen.has(value)) return '[Circular]';
    seen.add(value);
    const normalized = Object.fromEntries(Object.keys(value).sort((a, b) => a.localeCompare(b))
      .map((key) => [key, normalizeForStableJson(value[key], seen)]));
    seen.delete(value);
    return normalized;
  }
  return value;
}

/** Converts arbitrary backend values to safe deterministic plain text. */
export function formatFoodMakerValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value);
  try { return JSON.stringify(normalizeForStableJson(value, new WeakSet<object>())); } catch { return String(value); }
}

/** Formats full modal content without interpreting backend HTML. */
export function formatExpandedFoodMakerValue(value: unknown): string {
  if (value == null || value === '') return 'No value';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    try { return JSON.stringify(normalizeForStableJson(value, new WeakSet<object>()), null, 2); } catch { return String(value); }
  }
  return formatFoodMakerValue(value);
}

export function formatFoodMakerColumnLabel(key: string): string {
  return key.split('_').filter(Boolean).map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`).join(' ');
}

export function isBooleanFoodMakerColumn(records: FoodMakerApiRecord[], key: string): boolean {
  const values = records.map((record) => record[key]).filter((value) => value != null);
  return values.length > 0 && values.every((value) => typeof value === 'boolean');
}

function compareValues(left: unknown, right: unknown): number {
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  if (typeof left === 'boolean' && typeof right === 'boolean') return Number(left) - Number(right);
  return formatFoodMakerValue(left).localeCompare(formatFoodMakerValue(right), undefined, { numeric: true, sensitivity: 'base' });
}

function compareRecords(left: FoodMakerApiRecord, right: FoodMakerApiRecord, leftIndex: number, rightIndex: number, sort: FoodMakersSortConfig): number {
  const leftValue = left[sort.key];
  const rightValue = right[sort.key];
  const leftMissing = leftValue == null || formatFoodMakerValue(leftValue).length === 0;
  const rightMissing = rightValue == null || formatFoodMakerValue(rightValue).length === 0;
  if (leftMissing !== rightMissing) return leftMissing ? 1 : -1;
  if (!leftMissing && !rightMissing) {
    const result = compareValues(leftValue, rightValue);
    if (result !== 0) return result * (sort.direction === 'asc' ? 1 : -1);
  }
  return leftIndex - rightIndex;
}

/** Applies desktop AND filters and an optional stable column sort. */
export function filterAndSortFoodMakers(records: FoodMakerApiRecord[], filters: Record<string, string>, sortConfig: FoodMakersSortConfig | null): FoodMakerApiRecord[] {
  const activeFilters = Object.entries(filters)
    .map(([key, value]) => [key, value.trim().toLocaleLowerCase()] as const)
    .filter(([, value]) => value.length > 0 && value !== 'all');
  const filtered = records.map((record, originalIndex) => ({ record, originalIndex }))
    .filter(({ record }) => activeFilters.every(([key, filterValue]) => {
      const value = record[key];
      return filterValue === 'empty'
        ? value == null || formatFoodMakerValue(value).length === 0
        : formatFoodMakerValue(value).toLocaleLowerCase().includes(filterValue);
    }));
  if (sortConfig) filtered.sort((left, right) => compareRecords(left.record, right.record, left.originalIndex, right.originalIndex, sortConfig));
  return filtered.map(({ record }) => record);
}

export function getFoodMakerRowKey(record: FoodMakerApiRecord, index: number): string {
  const key = record.food_maker_key;
  if (typeof key === 'string' && key.trim()) return key;
  const name = record.name_code;
  if (typeof name === 'string' && name.trim()) return `name:${name}`;
  return `row:${index}`;
}

export function getFoodMakerDisplayName(record: FoodMakerApiRecord): string {
  if (typeof record.name_code === 'string' && record.name_code.trim()) return record.name_code;
  if (typeof record.food_maker_key === 'string' && record.food_maker_key.trim()) return record.food_maker_key;
  return 'Unnamed food maker';
}

export function getFoodMakerLocation(record: FoodMakerApiRecord): string {
  return formatFoodMakerValue(record.location).trim() || UNSPECIFIED_FOOD_MAKER_LOCATION;
}

/** Groups records by alphabetical location; partner rows are no longer manually reorderable. */
export function groupFoodMakersByLocation(records: FoodMakerApiRecord[], rowOrderByLocation: Record<string, string[]>, sortConfig: FoodMakersSortConfig | null): FoodMakerLocationGroup[] {
  const entries = new Map<string, Array<{ record: FoodMakerApiRecord; index: number; rowKey: string }>>();
  records.forEach((record, index) => {
    const location = getFoodMakerLocation(record);
    const group = entries.get(location) ?? [];
    group.push({ record, index, rowKey: getFoodMakerRowKey(record, index) });
    entries.set(location, group);
  });
  return Array.from(entries.entries()).sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .map(([location, group]) => {
      if (sortConfig) {
        group.sort((left, right) => compareRecords(left.record, right.record, left.index, right.index, sortConfig));
      }
      return { location, records: group.map((entry) => entry.record) };
    });
}

/** Matches a mobile search query against the display name and every formatted backend field. */
export function matchesFoodMakerSearch(record: FoodMakerApiRecord, query: string): boolean {
  const normalized = query.trim().toLocaleLowerCase();
  return !normalized || [getFoodMakerDisplayName(record), ...Object.values(record).map(formatFoodMakerValue)]
    .some((value) => value.toLocaleLowerCase().includes(normalized));
}

function parseSizeMap(candidate: unknown, minimum: number, maximum: number): Record<string, number> {
  if (!isRecord(candidate)) return {};
  return Object.fromEntries(Object.entries(candidate).filter((entry): entry is [string, number] => (
    typeof entry[1] === 'number' && Number.isFinite(entry[1]) && entry[1] >= minimum && entry[1] <= maximum
  )));
}

function parseStringList(candidate: unknown): string[] {
  return Array.isArray(candidate) ? candidate.filter((value, index): value is string => (
    typeof value === 'string' && value.length > 0 && candidate.indexOf(value) === index
  )) : [];
}

function parseRowOrderMap(candidate: unknown): Record<string, string[]> {
  if (!isRecord(candidate)) return {};
  return Object.fromEntries(Object.entries(candidate)
    .map(([location, order]) => [location, parseStringList(order)] as const)
    .filter(([location, order]) => location.length > 0 && order.length > 0));
}

/** Reads v2 layouts defensively and also accepts legacy v1 width/height data. */
export function parseFoodMakersTableLayout(storedValue: string | null): FoodMakersTableLayout {
  if (!storedValue) return EMPTY_TABLE_LAYOUT;
  try {
    const parsed = JSON.parse(storedValue) as unknown;
    if (!isRecord(parsed)) return EMPTY_TABLE_LAYOUT;
    return {
      columnWidths: parseSizeMap(parsed.columnWidths, MINIMUM_FOOD_MAKER_COLUMN_WIDTH, MAXIMUM_FOOD_MAKER_COLUMN_WIDTH),
      rowHeights: parseSizeMap(parsed.rowHeights, MINIMUM_FOOD_MAKER_ROW_HEIGHT, MAXIMUM_FOOD_MAKER_ROW_HEIGHT),
      columnOrder: parseStringList(parsed.columnOrder),
      mobileFieldOrder: parseStringList(parsed.mobileFieldOrder),
      // Row ordering is no longer configurable; discard legacy saved row order.
      rowOrderByLocation: {},
    };
  } catch { return EMPTY_TABLE_LAYOUT; }
}
